import { useEffect } from "react";
import { api } from "@/lib/api";

// Lightweight SEO tag manager. Fetches per-page overrides from admin, falls back to defaults.
export default function Seo({ page, title, description, keywords, canonical, ogImage, imageAlt, focusKeyword }) {
  useEffect(() => {
    let cancelled = false;

    const setMeta = (name, val, prop = false) => {
      if (!val) return;
      const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        prop ? el.setAttribute("property", name) : el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", val);
    };

    const setCanonical = (href) => {
      if (!href) return;
      let url = href.trim();
      if (!/^https?:\/\//i.test(url)) {
        url = `${window.location.origin}${url.startsWith("/") ? "" : "/"}${url}`;
      }
      let el = document.querySelector('link[rel="canonical"]');
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        document.head.appendChild(el);
      }
      el.setAttribute("href", url);
    };

    const apply = (s) => {
      if (cancelled) return;
      const t = s.title;
      const d = s.description;
      // Fold focus keyword into keywords meta if present
      const kw = [s.focus_keyword, s.keywords].filter(Boolean).join(", ");
      if (t) {
        document.title = t;
        setMeta("og:title", t, true);
        setMeta("twitter:title", t);
      }
      setMeta("description", d);
      setMeta("og:description", d, true);
      setMeta("twitter:description", d);
      setMeta("keywords", kw);
      if (s.og_image) {
        setMeta("og:image", s.og_image, true);
        setMeta("twitter:image", s.og_image);
        setMeta("twitter:card", "summary_large_image");
      }
      setMeta("og:image:alt", s.image_alt, true);
      setCanonical(s.canonical);
    };

    // Apply route-level defaults immediately
    apply({ title, description, keywords, canonical, og_image: ogImage, image_alt: imageAlt, focus_keyword: focusKeyword });

    // Then overlay admin-managed overrides
    if (page) {
      api.get(`/seo/${page}`).then((res) => {
        const s = res.data || {};
        apply({
          title: s.title || title,
          description: s.description || description,
          keywords: s.keywords || keywords,
          focus_keyword: s.focus_keyword || "",
          canonical: s.canonical || "",
          og_image: s.og_image || "",
          image_alt: s.image_alt || "",
        });
      }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [page, title, description, keywords, canonical, ogImage, imageAlt, focusKeyword]);
  return null;
}
